import React, { useState } from 'react';
import { DemoNoticeBanner } from './DemoNoticeBanner';
import { 
  Cloud, 
  Zap, 
  Database, 
  Radio, 
  FileCheck,
  ChevronDown,
  ChevronUp,
  Layers,
  Sparkles
} from 'lucide-react';

type Role = 'owner' | 'admin' | 'operator' | 'viewer';

export const EnterpriseArch: React.FC<{
  session?: React.ComponentProps<typeof DemoNoticeBanner>['session'];
  onSignIn?: () => void;
  onSwitchRole?: (role: Role) => void;
}> = ({ session = null, onSignIn, onSwitchRole }) => {
  const [archOpen, setArchOpen] = useState(true);
  const [lifecycleOpen, setLifecycleOpen] = useState(true);
  const [activePhase, setActivePhase] = useState<'all' | 'design' | 'validation' | 'runtime'>('all');

  const lifecycleSteps = [
    { step: '1', phase: 'design', title: 'Agent Idea Conceived', desc: 'Developer proposes an AI financial invoice & payment agent.' },
    { step: '2', phase: 'design', title: 'Risk Discovery', desc: 'AgenticScale identifies financial impact, prompt injection, and vendor mutation risks.' },
    { step: '3', phase: 'design', title: 'Safeguards Generated', desc: 'Dual-approval gates, phone verification, and $5k transaction limits configured.' },
    { step: '4', phase: 'design', title: 'Safety Profile Created', desc: 'Structured governance profile stored permanently in Cloudflare D1.' },
    { step: '5', phase: 'validation', title: 'Pre-Release Validated', desc: 'Behavioural test suites check the boundaries before the agent is released.' },
    { step: '6', phase: 'runtime', title: 'Adversarial Attack Intercepted', desc: 'Agent encounters urgent fraudulent bank update and injected memo.' },
    { step: '7', phase: 'runtime', title: 'In-Flight Gateway Evaluation', desc: 'AgenticScale Gateway intercepts action and flags REVIEW / BLOCK at edge.' },
    { step: '8', phase: 'runtime', title: 'Runbook & Human Decision', desc: 'Incident created with a runbook; authorized operators can acknowledge, approve, reject, or resolve it.' },
    { step: '9', phase: 'runtime', title: 'Fleet Observability', desc: 'Central dashboard aggregates D1 safety posture through short-interval polling.' },
  ];

  const filteredSteps = activePhase === 'all' 
    ? lifecycleSteps 
    : lifecycleSteps.filter(s => s.phase === activePhase);

  return (
    <div className="space-y-8 animate-fade-in max-w-6xl mx-auto">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Running it for a team
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm mt-1.5 max-w-3xl">
          One machine is the default, and it is enough for one person or one team's agents. If an
          organization needs a shared deployment instead, these are the pieces and what they map onto.
        </p>
      </div>

      {/* Who is allowed in, and why this site has no sign-in button.
          A visitor who sees "Sign in" on a demo concludes the product needs an
          account. It does not, and saying so once here is worth more than a
          button that leads to a login wall for an organization they will never
          be a member of. */}
      <section className="glass-panel rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Who is allowed in</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
            Three different answers, depending on where it is running. Only the middle one involves signing
            in to anything, which is why this site has no sign-in button in its menu.
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <p className="text-[11px] font-bold uppercase tracking-wide text-brand-600 dark:text-brand-400">On your own machine</p>
            <p className="mt-1.5 text-sm font-bold text-slate-900 dark:text-white">No sign-in at all</p>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
              The hub listens on 127.0.0.1. Reaching a loopback port already means being at the machine, so a
              password would protect nothing and would only be one more thing to lose. There is no account, no
              key and no login screen.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">Shared, for a team</p>
            <p className="mt-1.5 text-sm font-bold text-slate-900 dark:text-white">Your identity provider decides</p>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
              Identity comes from whatever your organization already uses, in front of the application — so it
              never sees a password and has no password to leak. Members carry one of four roles, and the roles
              are enforced on the server, with tests in CI that try to cross them and must fail.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white/60 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">On this website</p>
            <p className="mt-1.5 text-sm font-bold text-slate-900 dark:text-white">Switched off</p>
            <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
              Sign-in is not configured on this demonstration, and no identity is accepted — which is the safe
              direction for it to fail in. Everything here is readable without an account, and nothing you do
              on it is written to anybody's records.
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The four roles are owner, admin, operator and viewer: who can change a safety profile, who can only
          approve what is waiting, and who can only read. A visitor to this site is none of them.
        </p>

        {/* Try it, having been told what it is. This control used to sit at the
            top of every page with no explanation at all. */}
        {onSignIn && onSwitchRole && (
          <div className="rounded-xl border border-dashed border-slate-300 p-3 dark:border-slate-700">
            <p className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
              See what each role can do
            </p>
            <p className="mb-3 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Pick one and the menus change to what that role is allowed to reach. This is a preview of the
              shared deployment, on a site where nobody is signed in.
            </p>
            <DemoNoticeBanner session={session} onSignIn={onSignIn} onSwitchRole={onSwitchRole} />
          </div>
        )}
      </section>

      {/* Cloudflare vs Enterprise Mapping Table (Collapsible) */}
      <div className="glass-panel rounded-2xl overflow-hidden transition-all">
        <button
          type="button"
          onClick={() => setArchOpen(!archOpen)}
          className="w-full p-6 sm:p-8 flex items-center justify-between text-left hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Platform Architecture Overview</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">What this demo runs on, and the equivalent in a stack you may already have</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-400">
            <span className="text-xs font-semibold">{archOpen ? 'Collapse' : 'Expand'}</span>
            {archOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
        
        {archOpen && (
          <div className="p-6 sm:p-8 pt-0 border-t border-slate-100 dark:border-slate-800 space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-slate-500 dark:text-slate-400 uppercase font-bold text-[10px] border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Architecture Layer</th>
                    <th className="py-3 px-4 text-brand-700 dark:text-brand-400 font-bold">Current Deployment</th>
                    <th className="py-3 px-4 text-indigo-700 dark:text-indigo-400 font-bold">Enterprise Expansion</th>
                    <th className="py-3 px-4">Safety Benefit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium text-slate-700 dark:text-slate-300">
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                      <Cloud className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      <span>Frontend Dashboard</span>
                    </td>
                    <td className="py-3.5 px-4 text-brand-700 dark:text-brand-300 font-mono">Cloudflare Pages (Global CDN)</td>
                    <td className="py-3.5 px-4 text-indigo-700 dark:text-indigo-300 font-mono">Cloudflare Pages / AWS S3 + CloudFront</td>
                    <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">Edge-hosted access; measure performance per deployment</td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                      <span>API & Safety Gateway</span>
                    </td>
                    <td className="py-3.5 px-4 text-brand-700 dark:text-brand-300 font-mono">Cloudflare Workers / Pages Functions</td>
                    <td className="py-3.5 px-4 text-indigo-700 dark:text-indigo-300 font-mono">Cloudflare Workers + AWS Lambda</td>
                    <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">Deterministic policy enforcement with explicit auth and error boundaries</td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                      <Database className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span>Database & Storage</span>
                    </td>
                    <td className="py-3.5 px-4 text-brand-700 dark:text-brand-300 font-mono">Cloudflare D1 (SQLite Edge DB) + R2</td>
                    <td className="py-3.5 px-4 text-indigo-700 dark:text-indigo-300 font-mono">Cloudflare D1 + AWS DynamoDB + Aurora Serverless</td>
                    <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">Decisions stored in D1; archiving to object storage is built, not switched on</td>
                  </tr>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-white flex items-center space-x-2">
                      <Radio className="w-4 h-4 text-rose-500 dark:text-rose-400" />
                      <span>Event Telemetry & Alerting</span>
                    </td>
                    <td className="py-3.5 px-4 text-brand-700 dark:text-brand-300 font-mono">D1 telemetry + optional alert webhook</td>
                    <td className="py-3.5 px-4 text-indigo-700 dark:text-indigo-300 font-mono">AWS EventBridge + SNS + OpenTelemetry (OTel)</td>
                    <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">Recorded incidents with optional outbound notification</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 9-Step Continuous Assurance Lifecycle (Collapsible) */}
      <div className="glass-panel rounded-2xl overflow-hidden transition-all">
        <button
          type="button"
          onClick={() => setLifecycleOpen(!lifecycleOpen)}
          className="w-full p-6 sm:p-8 flex items-center justify-between text-left hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">9-Step Continuous Safety Lifecycle</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">End-to-end framework from concept ideation to edge defense</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-400">
            <span className="text-xs font-semibold">{lifecycleOpen ? 'Collapse' : 'Expand'}</span>
            {lifecycleOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {lifecycleOpen && (
          <div className="p-6 sm:p-8 pt-0 border-t border-slate-100 dark:border-slate-800 space-y-6">
            
            {/* Phase Filters */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All 9 Steps' },
                { id: 'design', label: 'Phase 1: Design & Safeguards (Steps 1-4)' },
                { id: 'validation', label: 'Phase 2: Pre-Release (Step 5)' },
                { id: 'runtime', label: 'Phase 3: Runtime Defense (Steps 6-9)' }
              ].map((phase) => (
                <button
                  key={phase.id}
                  type="button"
                  onClick={() => setActivePhase(phase.id as any)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    activePhase === phase.id
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {phase.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              {filteredSteps.map((item) => (
                <div key={item.step} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 space-y-1.5 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-800 dark:bg-brand-500/20 dark:text-brand-300 border border-brand-300 dark:border-brand-500/30 flex items-center justify-center font-mono font-bold text-[10px]">
                      {item.step}
                    </span>
                    <span className="font-bold text-slate-900 dark:text-white">{item.title}</span>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 text-[11px] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800/40 text-xs text-brand-900 dark:text-brand-200 font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
              <span><strong className="text-slate-900 dark:text-white">Guiding Principle:</strong> AI agents accelerate critical business processes, but organizations need continuous safety assurance to scale AI adoption responsibly.</span>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
